import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { NAV, SITE } from "@/lib/site";

const Logo = ({ light }) => (
  <Link to="/" className="flex items-center" data-testid="header-logo-link" aria-label="Alpha Transfer home">
    <img src={light ? SITE.logoLight : SITE.logoDark} alt="Alpha Transfer logo" className="h-9 w-auto md:h-10" />
  </Link>
);

const MobileMenu = ({ open, onClose }) => (
  <div
    className={`fixed inset-x-0 bottom-0 top-[72px] z-40 overflow-y-auto bg-white pb-10 transition-[opacity,transform] duration-300 lg:hidden ${
      open ? "visible pointer-events-auto translate-y-0 opacity-100" : "invisible pointer-events-none -translate-y-3 opacity-0"
    }`}
    data-testid="mobile-menu"
  >
    <nav className="container-x flex flex-col pt-6">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end
          onClick={onClose}
          data-testid={`mobile-nav-${n.label.toLowerCase()}`}
          className={({ isActive }) =>
            `border-b border-slate-100 py-5 font-serif text-3xl ${isActive ? "text-gold-hover" : "text-ink"}`
          }
        >
          {n.label}
        </NavLink>
      ))}
      <Link to="/booking" onClick={onClose} className="btn btn-gold mt-8" data-testid="mobile-book-now-button">Book Now</Link>
      <a href={SITE.phoneHref} className="btn btn-outline mt-3" data-testid="mobile-call-button">
        <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
      </a>
    </nav>
  </div>
);

export const Header = () => {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const overHero = pathname === "/" && !scrolled && !open;
  const solid = open ? "border-b border-slate-200/70 bg-white" : "border-b border-slate-200/70 bg-white/85 backdrop-blur-xl";
  const linkColor = overHero ? "text-white/85 hover:text-white" : "text-slate-600 hover:text-ink";

  return (
    <>
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-300 ${
        overHero ? "border-b border-transparent bg-transparent" : solid
      }`}
    >
      <div className="container-x flex h-[72px] items-center justify-between gap-6">
        <Logo light={overHero} />
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Main navigation">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-[13px] font-medium tracking-wide transition-colors ${linkColor} ${
                  isActive ? "after:absolute after:-bottom-2 after:left-0 after:h-px after:w-full after:bg-gold" : ""
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/booking" className="btn btn-gold hidden min-h-[44px] px-6 sm:inline-flex" data-testid="nav-book-now-button">
            Book Now
          </Link>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className={`flex h-11 w-11 items-center justify-center rounded-full lg:hidden ${overHero ? "text-white" : "text-ink"}`}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
    </header>
    <MobileMenu open={open} onClose={() => setOpen(false)} />
    </>
  );
};
